import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { isApiProblem } from "../shared/api/problem";
import { notifySessionExpired } from "./session/sessionEvents";

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      authFlow?: boolean;
      /** The work item key a move or check targets (TransitionControls), so consumers can tell which item a pending or finished mutation belongs to. */
      itemRef?: string;
    };
  }
}

function shouldRetry(failureCount: number, error: unknown): boolean {
  // 4xx answers are decisions, not glitches; retry only network failures and 5xx.
  if (isApiProblem(error) && error.status >= 400 && error.status < 500) return false;
  return failureCount < 2;
}

export function createQueryClient(options: { retry?: boolean } = {}): QueryClient {
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError(error, query) {
        if (isApiProblem(error) && error.isUnauthorized && query.queryKey[0] !== "me") notifySessionExpired();
      },
    }),
    mutationCache: new MutationCache({
      onError(error, _variables, _context, mutation) {
        if (isApiProblem(error) && error.isUnauthorized && !mutation.meta?.authFlow) notifySessionExpired();
        // Settings → Permissions (0.10.36): a 403 carries the matrix revision it was decided at. When it is not the one the
        // session was read at, a System administrator changed permissions since: refetch /auth/me so the screens hide what
        // the server now refuses.
        if (isApiProblem(error) && error.status === 403 && error.permissionsRevision !== undefined) {
          const me = client.getQueryData<{ permissionsRevision?: number }>(["me"]);
          if (me && (me.permissionsRevision ?? 0) !== error.permissionsRevision) void client.invalidateQueries({ queryKey: ["me"] });
        }
      },
    }),
    defaultOptions: {
      queries: { retry: options.retry === false ? false : shouldRetry, staleTime: 10_000, refetchOnWindowFocus: false },
      mutations: { retry: false },
    },
  });
  return client;
}
